import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  MegaphoneIcon,
  PaperAirplaneIcon,
  DocumentTextIcon,
  ClockIcon,
  QueueListIcon,
  BeakerIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  PencilIcon,
  PlayIcon,
  LockClosedIcon,
  PlusIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import {
  useUsersWithTokens,
  useTestHistory,
  useSendTest,
  useCampaigns,
  useCampaign,
  useCreateCampaign,
  useCancelCampaign,
  useSendCampaignNow,
  useQueue,
  useTemplates,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
} from '@/hooks/useMarketing';
import { useAuthStore } from '@/stores/authStore';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton, SkeletonTable } from '@/components/ui/Skeleton';
import { cn } from '@/utils/cn';
import { formatDate, formatRelativeTime } from '@/utils/format';

// ── Types ────────────────────────────────────────────────────

type TabKey = 'test' | 'compose' | 'templates' | 'history' | 'queue';

interface UserWithToken {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  hasActiveToken: boolean;
}

interface Campaign {
  id: string;
  title: string;
  body: string;
  channels: string[];
  status: string;
  targetType: string;
  totalRecipients: number;
  deliveredCount: number;
  failedCount: number;
  openedCount: number;
  isTest: boolean;
  scheduledAt: string | null;
  sentAt: string | null;
  createdAt: string;
}

interface Template {
  id: string;
  name: string;
  titleTemplate: string;
  bodyTemplate: string;
  category: string;
  variables: string[];
  isSystem: boolean;
  channels: string[];
}

// ── Status Badge ─────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    delivered: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    sent: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    sending: 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400',
    scheduled: 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  };
  return (
    <span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', colors[status] ?? colors.draft)}>
      {status}
    </span>
  );
}

function ChannelBadges({ channels }: { channels: string[] }) {
  const labels: Record<string, string> = { push: 'Push', email: 'Email', panel: 'Panel' };
  return (
    <div className="flex gap-1">
      {channels.map((ch) => (
        <span key={ch} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
          {labels[ch] ?? ch}
        </span>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  Main Component
// ══════════════════════════════════════════════════════════════

export function AdminMarketingPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>('test');

  const tabs: Array<{ key: TabKey; label: string; icon: typeof MegaphoneIcon }> = [
    { key: 'test', label: t('marketing.test'), icon: BeakerIcon },
    { key: 'compose', label: t('marketing.compose'), icon: PaperAirplaneIcon },
    { key: 'templates', label: t('marketing.templates'), icon: DocumentTextIcon },
    { key: 'history', label: t('marketing.history'), icon: ClockIcon },
    { key: 'queue', label: t('marketing.queue'), icon: QueueListIcon },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('marketing.title')}</h1>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors',
                activeTab === tab.key
                  ? 'bg-white shadow-sm dark:bg-gray-700 dark:text-gray-100'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
              )}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'test' && <TestTab />}
      {activeTab === 'compose' && <ComposeTab />}
      {activeTab === 'templates' && <TemplatesTab />}
      {activeTab === 'history' && <HistoryTab />}
      {activeTab === 'queue' && <QueueTab />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 1: TEST
// ══════════════════════════════════════════════════════════════

function TestTab() {
  const { t } = useTranslation();
  const { data: usersData, isLoading: usersLoading } = useUsersWithTokens();
  const { data: historyData, isLoading: historyLoading } = useTestHistory();
  const sendTest = useSendTest();
  const currentUser = useAuthStore((s) => s.user);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [channels, setChannels] = useState<string[]>(['push', 'panel']);
  const [deepLinkType, setDeepLinkType] = useState<'none' | 'dashboard' | 'order' | 'url'>('none');
  const [deepLinkValue, setDeepLinkValue] = useState('');
  const [result, setResult] = useState<Array<{ channel: string; status: string; error?: string }> | null>(null);

  const toggleChannel = (ch: string) => {
    setChannels((prev) => prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]);
  };

  const getDeepLink = () => {
    if (deepLinkType === 'dashboard') return 'wooconnect://dashboard';
    if (deepLinkType === 'order' && deepLinkValue) return `wooconnect://orders/${deepLinkValue}`;
    if (deepLinkType === 'url' && deepLinkValue) return deepLinkValue;
    return undefined;
  };

  const handleSend = async () => {
    if (!selectedUserId || !title || !body || channels.length === 0) return;
    setResult(null);
    try {
      const res = await sendTest.mutateAsync({
        userId: selectedUserId,
        title,
        body,
        channels,
        deepLink: getDeepLink(),
      });
      setResult(res.results);
      toast.success(t('marketing.testSentSuccess'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const users = (usersData ?? []) as UserWithToken[];
  const history = (historyData ?? []) as Campaign[];

  return (
    <div className="space-y-6">
      <Card title={t('marketing.testSend')}>
        <div className="space-y-4">
          {/* Recipient */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.recipient')}</label>
            {usersLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="">{t('marketing.selectUser')}</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} ({u.email}) {u.hasActiveToken ? '' : `[${t('marketing.noPushToken')}]`}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Channels */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.channels')}</label>
            <div className="flex gap-4">
              {(['push', 'email', 'panel'] as const).map((ch) => (
                <label key={ch} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={channels.includes(ch)} onChange={() => toggleChannel(ch)} className="rounded border-gray-300" />
                  {t(`marketing.${ch}`)}
                </label>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.messageTitle')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              placeholder={t('marketing.messageTitle')}
            />
          </div>

          {/* Body */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.messageBody')}</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              placeholder={t('marketing.messageBody')}
            />
          </div>

          {/* Deep Link */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.deepLink')}</label>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <select
                value={deepLinkType}
                onChange={(e) => { setDeepLinkType(e.target.value as typeof deepLinkType); setDeepLinkValue(''); }}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="none">{t('marketing.deepLinkNone')}</option>
                <option value="dashboard">{t('marketing.deepLinkDashboard')}</option>
                <option value="order">{t('marketing.deepLinkOrder')}</option>
                <option value="url">{t('marketing.deepLinkUrl')}</option>
              </select>
              {(deepLinkType === 'order' || deepLinkType === 'url') && (
                <input
                  type="text"
                  value={deepLinkValue}
                  onChange={(e) => setDeepLinkValue(e.target.value)}
                  placeholder={deepLinkType === 'order' ? '#12345' : 'https://...'}
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              )}
            </div>
          </div>

          <Button onClick={handleSend} loading={sendTest.isPending} disabled={!selectedUserId || !title || !body || channels.length === 0}>
            {t('marketing.sendTest')}
          </Button>
        </div>

        {/* Result */}
        {result && (
          <div className="mt-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <p className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">{t('marketing.result')}</p>
            <div className="space-y-1">
              {result.map((r) => (
                <div key={r.channel} className="flex items-center gap-2 text-sm">
                  {r.status === 'delivered' ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 text-red-500" />
                  )}
                  <span className="font-medium text-gray-700 dark:text-gray-300">{r.channel}:</span>
                  <span className={r.status === 'delivered' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                    {r.status === 'delivered' ? t('marketing.delivered') : t('marketing.failed')}
                  </span>
                  {r.error && <span className="text-xs text-gray-400">({r.error})</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Test History */}
      <Card title={t('marketing.testHistory')}>
        {historyLoading ? (
          <SkeletonTable rows={5} cols={5} />
        ) : history.length > 0 ? (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-5 py-2">{t('marketing.date')}</th>
                  <th className="px-5 py-2">{t('marketing.messageTitle')}</th>
                  <th className="px-5 py-2">{t('marketing.channels')}</th>
                  <th className="px-5 py-2">{t('marketing.status')}</th>
                  <th className="px-5 py-2 text-right">{t('marketing.deliveredCount')}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item.id} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="px-5 py-3 text-gray-500">{formatRelativeTime(item.createdAt)}</td>
                    <td className="px-5 py-3 font-medium text-gray-900 dark:text-gray-100">{item.title}</td>
                    <td className="px-5 py-3"><ChannelBadges channels={item.channels} /></td>
                    <td className="px-5 py-3"><StatusBadge status={item.status} /></td>
                    <td className="px-5 py-3 text-right text-gray-600 dark:text-gray-300">{item.deliveredCount}/{item.totalRecipients}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon={BeakerIcon} title={t('marketing.noTestHistory')} />
        )}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 2: COMPOSE
// ══════════════════════════════════════════════════════════════

function ComposeTab() {
  const { t } = useTranslation();
  const { data: usersData, isLoading: usersLoading } = useUsersWithTokens();
  const { data: templatesData } = useTemplates();
  const currentUser = useAuthStore((s) => s.user);
  const createCampaign = useCreateCampaign();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [channels, setChannels] = useState<string[]>(['push', 'panel']);
  const [targetType, setTargetType] = useState<'all' | 'role' | 'individual'>('all');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [sendMode, setSendMode] = useState<'now' | 'scheduled'>('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [deepLinkType, setDeepLinkType] = useState<'none' | 'dashboard' | 'order' | 'url'>('none');
  const [deepLinkValue, setDeepLinkValue] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const users = (usersData ?? []) as UserWithToken[];
  const templates = (templatesData ?? []) as Template[];
  const roles = ['super_admin', 'store_owner', 'manager', 'staff'];
  const usersWithPush = users.filter((u) => u.hasActiveToken).length;

  const toggleChannel = (ch: string) => {
    setChannels((prev) => prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]);
  };

  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) => prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]);
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (templateId) {
      const tpl = templates.find((t) => t.id === templateId);
      if (tpl) {
        setTitle(tpl.titleTemplate);
        setBody(tpl.bodyTemplate);
      }
    }
  };

  const getDeepLink = () => {
    if (deepLinkType === 'dashboard') return 'wooconnect://dashboard';
    if (deepLinkType === 'order' && deepLinkValue) return `wooconnect://orders/${deepLinkValue}`;
    if (deepLinkType === 'url' && deepLinkValue) return deepLinkValue;
    return undefined;
  };

  const getTargetFilter = () => {
    if (targetType === 'role') return { role: selectedRole };
    if (targetType === 'individual') return { userIds: selectedUserIds };
    return undefined;
  };

  const handleSend = async () => {
    if (!title || !body || channels.length === 0) return;

    const scheduledAt = sendMode === 'scheduled' && scheduledDate
      ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString()
      : undefined;

    try {
      await createCampaign.mutateAsync({
        title,
        body,
        channels,
        targetType,
        targetFilter: getTargetFilter(),
        deepLink: getDeepLink(),
        scheduledAt,
        templateId: selectedTemplateId || undefined,
      });
      toast.success(sendMode === 'scheduled' ? t('marketing.scheduled') : t('marketing.campaignSent'));
      // Reset form
      setTitle(''); setBody(''); setSelectedTemplateId('');
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleTestToMe = async () => {
    if (!currentUser || !title || !body) return;
    try {
      const { sendTestNotification } = await import('@/services/marketing.api');
      await sendTestNotification({
        userId: currentUser.id,
        title,
        body,
        channels,
        deepLink: getDeepLink(),
      });
      toast.success(t('marketing.testSentSuccess'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* Main form */}
      <div className="space-y-6 lg:col-span-2">
        <Card title={t('marketing.newCampaign')}>
          <div className="space-y-4">
            {/* Template selector */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.template')}</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="">{t('marketing.noTemplate')}</option>
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>{tpl.name} ({tpl.category})</option>
                ))}
              </select>
            </div>

            {/* Title + Body */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.messageTitle')}</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.messageBody')}</label>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
            </div>

            {/* Recipients */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.recipients')}</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="radio" name="target" checked={targetType === 'all'} onChange={() => setTargetType('all')} />
                  {t('marketing.allActiveUsers')} ({users.length})
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="radio" name="target" checked={targetType === 'role'} onChange={() => setTargetType('role')} />
                  {t('marketing.byRole')}
                </label>
                {targetType === 'role' && (
                  <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} className="ml-6 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                    <option value="">{t('marketing.selectRole')}</option>
                    {roles.map((r) => (
                      <option key={r} value={r}>{r.replace('_', ' ')}</option>
                    ))}
                  </select>
                )}
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="radio" name="target" checked={targetType === 'individual'} onChange={() => setTargetType('individual')} />
                  {t('marketing.specificUsers')}
                </label>
                {targetType === 'individual' && (
                  <div className="ml-6 max-h-40 overflow-y-auto rounded-lg border border-gray-200 p-2 dark:border-gray-700">
                    {users.map((u) => (
                      <label key={u.id} className="flex items-center gap-2 py-1 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={selectedUserIds.includes(u.id)} onChange={() => toggleUser(u.id)} className="rounded border-gray-300" />
                        {u.firstName} {u.lastName} ({u.email})
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Channels */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.channels')}</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={channels.includes('push')} onChange={() => toggleChannel('push')} className="rounded border-gray-300" />
                  {t('marketing.push')} ({usersWithPush})
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={channels.includes('email')} onChange={() => toggleChannel('email')} className="rounded border-gray-300" />
                  {t('marketing.email')}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={channels.includes('panel')} onChange={() => toggleChannel('panel')} className="rounded border-gray-300" />
                  {t('marketing.panelNotification')}
                </label>
              </div>
            </div>

            {/* Send mode */}
            <div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="radio" name="sendMode" checked={sendMode === 'now'} onChange={() => setSendMode('now')} />
                  {t('marketing.sendNow')}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="radio" name="sendMode" checked={sendMode === 'scheduled'} onChange={() => setSendMode('scheduled')} />
                  {t('marketing.scheduleFor')}
                </label>
              </div>
              {sendMode === 'scheduled' && (
                <div className="mt-2 flex gap-2">
                  <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
                  <input type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
                </div>
              )}
            </div>

            {/* Deep Link */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.deepLink')}</label>
              <div className="flex gap-2">
                <select value={deepLinkType} onChange={(e) => { setDeepLinkType(e.target.value as typeof deepLinkType); setDeepLinkValue(''); }} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                  <option value="none">{t('marketing.deepLinkNone')}</option>
                  <option value="dashboard">{t('marketing.deepLinkDashboard')}</option>
                  <option value="order">{t('marketing.deepLinkOrder')}</option>
                  <option value="url">{t('marketing.deepLinkUrl')}</option>
                </select>
                {(deepLinkType === 'order' || deepLinkType === 'url') && (
                  <input type="text" value={deepLinkValue} onChange={(e) => setDeepLinkValue(e.target.value)} placeholder={deepLinkType === 'order' ? '#12345' : 'https://...'} className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
              <Button variant="secondary" onClick={handleTestToMe} disabled={!title || !body}>
                {t('marketing.testToMe')}
              </Button>
              <Button onClick={handleSend} loading={createCampaign.isPending} disabled={!title || !body || channels.length === 0}>
                {sendMode === 'scheduled' ? t('marketing.sendOrSchedule') : t('marketing.sendNow')}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Preview sidebar */}
      <div>
        <Card title={t('marketing.preview')}>
          <div className="space-y-4">
            {channels.includes('push') && (
              <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <p className="mb-1 text-xs font-semibold uppercase text-gray-400">{t('marketing.push')}</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{title || '...'}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{body ? (body.length > 100 ? body.slice(0, 100) + '...' : body) : '...'}</p>
              </div>
            )}
            {channels.includes('email') && (
              <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <p className="mb-1 text-xs font-semibold uppercase text-gray-400">{t('marketing.email')}</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{title || '...'}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{body || '...'}</p>
              </div>
            )}
            {channels.includes('panel') && (
              <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <p className="mb-1 text-xs font-semibold uppercase text-gray-400">{t('marketing.panelNotification')}</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{title || '...'}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{body || '...'}</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 3: TEMPLATES
// ══════════════════════════════════════════════════════════════

function TemplatesTab() {
  const { t } = useTranslation();
  const { data: templatesData, isLoading } = useTemplates();
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const deleteTemplate = useDeleteTemplate();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [titleTemplate, setTitleTemplate] = useState('');
  const [bodyTemplate, setBodyTemplate] = useState('');
  const [category, setCategory] = useState('custom');
  const [variables, setVariables] = useState('');

  const templates = (templatesData ?? []) as Template[];
  const categories = ['welcome', 'reminder', 'summary', 'promo', 'alert', 'custom'];

  const resetForm = () => {
    setShowForm(false); setEditingId(null);
    setName(''); setTitleTemplate(''); setBodyTemplate('');
    setCategory('custom'); setVariables('');
  };

  const startEdit = (tpl: Template) => {
    setEditingId(tpl.id);
    setName(tpl.name);
    setTitleTemplate(tpl.titleTemplate);
    setBodyTemplate(tpl.bodyTemplate);
    setCategory(tpl.category);
    setVariables(tpl.variables.join(', '));
    setShowForm(true);
  };

  const handleSave = async () => {
    const vars = variables.split(',').map((v) => v.trim()).filter(Boolean);
    try {
      if (editingId) {
        await updateTemplate.mutateAsync({ id: editingId, data: { name, titleTemplate, bodyTemplate, category, variables: vars } });
        toast.success(t('marketing.templateUpdated'));
      } else {
        await createTemplate.mutateAsync({ name, titleTemplate, bodyTemplate, category, variables: vars });
        toast.success(t('marketing.templateCreated'));
      }
      resetForm();
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('marketing.confirmDelete'))) return;
    try {
      await deleteTemplate.mutateAsync(id);
      toast.success(t('marketing.templateDeleted'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div />
        <Button onClick={() => { resetForm(); setShowForm(true); }}>
          <PlusIcon className="mr-1 h-4 w-4" /> {t('marketing.newTemplate')}
        </Button>
      </div>

      {/* Create/Edit form */}
      {showForm && (
        <Card title={editingId ? t('marketing.edit') : t('marketing.newTemplate')}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.templateName')}</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.templateCategory')}</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                  {categories.map((c) => (
                    <option key={c} value={c}>{t(`marketing.${c}`)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.messageTitle')}</label>
              <input type="text" value={titleTemplate} onChange={(e) => setTitleTemplate(e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.messageBody')}</label>
              <textarea value={bodyTemplate} onChange={(e) => setBodyTemplate(e.target.value)} rows={3} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.variables')} (comma-separated)</label>
              <input type="text" value={variables} onChange={(e) => setVariables(e.target.value)} placeholder="userName, storeName, orderCount" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} loading={createTemplate.isPending || updateTemplate.isPending} disabled={!name || !titleTemplate || !bodyTemplate}>
                {editingId ? t('marketing.save') : t('marketing.create')}
              </Button>
              <Button variant="secondary" onClick={resetForm}>{t('common.cancel')}</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Template list */}
      <Card>
        {isLoading ? (
          <SkeletonTable rows={5} cols={4} />
        ) : templates.length > 0 ? (
          <div className="space-y-3">
            {templates.map((tpl) => (
              <div key={tpl.id} className="flex items-start justify-between rounded-lg border border-gray-100 p-4 dark:border-gray-700">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {tpl.isSystem && <LockClosedIcon className="h-4 w-4 text-gray-400" />}
                    <p className="font-medium text-gray-900 dark:text-gray-100">{tpl.name}</p>
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                      {t(`marketing.${tpl.category}`, tpl.category)}
                    </span>
                    {tpl.isSystem && <span className="text-xs text-gray-400">{t('marketing.systemTemplate')}</span>}
                  </div>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{tpl.bodyTemplate.length > 80 ? tpl.bodyTemplate.slice(0, 80) + '...' : tpl.bodyTemplate}</p>
                  {tpl.variables.length > 0 && (
                    <p className="mt-1 text-xs text-gray-400">{t('marketing.variables')}: {tpl.variables.join(', ')}</p>
                  )}
                </div>
                {!tpl.isSystem && (
                  <div className="ml-4 flex gap-1">
                    <button onClick={() => startEdit(tpl)} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800">
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(tpl.id)} className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState icon={DocumentTextIcon} title={t('marketing.noTemplates')} />
        )}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 4: HISTORY
// ══════════════════════════════════════════════════════════════

function HistoryTab() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const { data, isLoading } = useCampaigns({ page, perPage: 20, status: statusFilter || undefined, isTest: false });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: detailData, isLoading: detailLoading } = useCampaign(selectedId);

  const campaigns = (data?.items ?? []) as Campaign[];
  const totalPages = data?.totalPages ?? 1;

  if (selectedId && detailData) {
    const detail = detailData as { campaign: Campaign; recipients: Array<{ id: string; email: string; firstName: string; lastName: string; channel: string; status: string; errorMessage: string | null }> };
    return (
      <div className="space-y-6">
        <Button variant="secondary" onClick={() => setSelectedId(null)}>{t('marketing.back')}</Button>
        <Card title={detail.campaign.title}>
          <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/50">
              <p className="text-xs text-gray-500">{t('marketing.totalRecipients')}</p>
              <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{detail.campaign.totalRecipients}</p>
            </div>
            <div className="rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
              <p className="text-xs text-gray-500">{t('marketing.deliveredCount')}</p>
              <p className="text-lg font-bold text-green-700 dark:text-green-400">{detail.campaign.deliveredCount}</p>
            </div>
            <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
              <p className="text-xs text-gray-500">{t('marketing.failedCount')}</p>
              <p className="text-lg font-bold text-red-700 dark:text-red-400">{detail.campaign.failedCount}</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/50">
              <p className="text-xs text-gray-500">{t('marketing.status')}</p>
              <StatusBadge status={detail.campaign.status} />
            </div>
          </div>

          <p className="mb-4 text-sm font-medium text-gray-700 dark:text-gray-300">{t('marketing.recipientResults')}</p>
          {detailLoading ? (
            <SkeletonTable rows={5} cols={4} />
          ) : (
            <div className="-mx-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-5 py-2">{t('marketing.recipient')}</th>
                    <th className="px-5 py-2">{t('marketing.channels')}</th>
                    <th className="px-5 py-2">{t('marketing.status')}</th>
                    <th className="px-5 py-2">{t('common.error')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.recipients.map((r) => (
                    <tr key={r.id} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="px-5 py-3">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{r.firstName} {r.lastName}</p>
                        <p className="text-xs text-gray-400">{r.email}</p>
                      </td>
                      <td className="px-5 py-3"><ChannelBadges channels={[r.channel]} /></td>
                      <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-5 py-3 text-xs text-gray-400">{r.errorMessage ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
        >
          <option value="">{t('common.all')}</option>
          <option value="sent">{t('marketing.sent')}</option>
          <option value="scheduled">{t('marketing.scheduled')}</option>
          <option value="sending">{t('marketing.sending')}</option>
          <option value="failed">{t('marketing.failed')}</option>
          <option value="cancelled">{t('marketing.cancelled')}</option>
        </select>
      </div>

      <Card>
        {isLoading ? (
          <SkeletonTable rows={10} cols={6} />
        ) : campaigns.length > 0 ? (
          <>
            <div className="-mx-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-5 py-2">{t('marketing.date')}</th>
                    <th className="px-5 py-2">{t('marketing.messageTitle')}</th>
                    <th className="px-5 py-2">{t('marketing.recipients')}</th>
                    <th className="px-5 py-2">{t('marketing.channels')}</th>
                    <th className="px-5 py-2">{t('marketing.status')}</th>
                    <th className="px-5 py-2 text-right">{t('marketing.deliveredCount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => setSelectedId(c.id)}
                      className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50"
                    >
                      <td className="px-5 py-3 text-gray-500">{formatRelativeTime(c.sentAt ?? c.createdAt)}</td>
                      <td className="px-5 py-3 font-medium text-gray-900 dark:text-gray-100">{c.title}</td>
                      <td className="px-5 py-3 text-gray-600 dark:text-gray-300">{c.totalRecipients}</td>
                      <td className="px-5 py-3"><ChannelBadges channels={c.channels} /></td>
                      <td className="px-5 py-3"><StatusBadge status={c.status} /></td>
                      <td className="px-5 py-3 text-right text-gray-600 dark:text-gray-300">{c.deliveredCount}/{c.totalRecipients}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
                <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>{t('common.back')}</Button>
                <span className="flex items-center text-sm text-gray-500">{page} / {totalPages}</span>
                <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>{t('common.next')}</Button>
              </div>
            )}
          </>
        ) : (
          <EmptyState icon={ClockIcon} title={t('marketing.noCampaigns')} />
        )}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 5: QUEUE
// ══════════════════════════════════════════════════════════════

function QueueTab() {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useQueue();
  const cancelCampaign = useCancelCampaign();
  const sendNow = useSendCampaignNow();

  const items = (data ?? []) as Campaign[];

  const handleCancel = async (id: string) => {
    if (!confirm(t('marketing.confirmCancel'))) return;
    try {
      await cancelCampaign.mutateAsync(id);
      toast.success(t('marketing.campaignCancelled'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleSendNow = async (id: string) => {
    if (!confirm(t('marketing.confirmSendNow'))) return;
    try {
      await sendNow.mutateAsync(id);
      toast.success(t('marketing.campaignSent'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  return (
    <Card title={t('marketing.scheduledMessages')}>
      {isLoading ? (
        <SkeletonTable rows={5} cols={5} />
      ) : items.length > 0 ? (
        <div className="-mx-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <th className="px-5 py-2">{t('marketing.messageTitle')}</th>
                <th className="px-5 py-2">{t('marketing.recipients')}</th>
                <th className="px-5 py-2">{t('marketing.channels')}</th>
                <th className="px-5 py-2">{t('marketing.scheduled')}</th>
                <th className="px-5 py-2">{t('marketing.status')}</th>
                <th className="px-5 py-2 text-right">{t('marketing.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-gray-50 dark:border-gray-800">
                  <td className="px-5 py-3 font-medium text-gray-900 dark:text-gray-100">{item.title}</td>
                  <td className="px-5 py-3 text-gray-600 dark:text-gray-300">{item.totalRecipients}</td>
                  <td className="px-5 py-3"><ChannelBadges channels={item.channels} /></td>
                  <td className="px-5 py-3 text-gray-500">{item.scheduledAt ? formatDate(item.scheduledAt, 'dd/MM HH:mm') : '-'}</td>
                  <td className="px-5 py-3"><StatusBadge status={item.status} /></td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      {item.status === 'scheduled' && (
                        <>
                          <button onClick={() => handleSendNow(item.id)} className="rounded-lg p-1.5 text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/20" title={t('marketing.sendImmediately')}>
                            <PlayIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleCancel(item.id)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title={t('marketing.cancel')}>
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState icon={QueueListIcon} title={t('marketing.noScheduled')} />
      )}
    </Card>
  );
}
